/**
 * Arousal Effect Detail Page
 * 
 * Displays full details for a single arousal effect (involuntary response) including:
 * - Name, category, onset badges
 * - Key stats (visibility, arousal threshold)
 * - Description and page sections
 * - Body applicability info
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft, Activity, Eye, Gauge, Clock,
    BookOpen, Sparkles, Heart, Users
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import Spinner from '../components/Spinner';

// Category colors for effects
const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
        case 'physical': return 'text-rose-400 bg-rose-500/10 border-rose-500/20';
        case 'emotional': return 'text-purple-400 bg-purple-500/10 border-purple-500/20';
        case 'behavioral': return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
        case 'cognitive': return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
        default: return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
};

// Onset timing badges
const getOnsetBadge = (onset) => {
    const colors = {
        pre: 'text-cyan-400 bg-cyan-500/10',
        during: 'text-amber-400 bg-amber-500/10',
        peak: 'text-rose-400 bg-rose-500/10',
        post: 'text-emerald-400 bg-emerald-500/10'
    };
    return colors[onset?.toLowerCase()] || 'text-gray-400 bg-gray-500/10';
};

// Page section component
const PageSection = ({ title, content, icon: Icon }) => {
    if (!content) return null;
    return (
        <section className="mb-6 p-5 rounded-xl bg-[#0A0A0A]/60 border border-wine-800/20">
            <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon className="w-5 h-5 text-[#C4A35A]" />}
                <h3 className="text-lg font-fantasy font-semibold text-cream/90 tracking-wide">{title}</h3>
            </div>
            <div className="prose prose-invert max-w-none">
                <p className="text-cream/70 leading-relaxed whitespace-pre-wrap">{content}</p>
            </div>
        </section>
    );
};

// Visibility bar
const VisibilityBar = ({ value }) => {
    const percentage = Math.min(100, (value / 10) * 100);
    return (
        <div className="flex items-center gap-3">
            <Eye size={16} className="text-cream/50" />
            <div className="flex-1 h-2 bg-dark-800 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <span className="text-sm text-cream/70 w-8">{value}/10</span>
        </div>
    );
};

const ArousalEffectDetailPage = () => {
    const { id } = useParams();
    const [effect, setEffect] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchEffect();
    }, [id]);

    const fetchEffect = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getArousalEffect(id);
            setEffect(data);
        } catch (err) {
            console.error('Error fetching arousal effect:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="xl" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading effect...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !effect) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">⚡</div>
                    <h2 className="text-xl font-bold text-white mb-2">Effect Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This arousal effect does not exist.'}</p>
                    <Link to="/effects" className="btn-primary">
                        Back to Effects
                    </Link>
                </div>
            </div>
        );
    }

    // Get page sections by key
    const getSectionContent = (key) => {
        const section = effect.pageSections?.find(s => s.sectionKey === key);
        return section?.contentFormatted || section?.content;
    };

    const categoryClass = getCategoryColor(effect.category);
    const onsetClass = getOnsetBadge(effect.onset);

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/effects"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Effects</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {effect.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/effect/${effect.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {effect.parentVariant.name}
                            <Activity size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <header className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-wine-700/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-wine-700/20 flex items-center justify-center">
                                <Activity size={28} className="text-wine-400" />
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-3 tracking-wide">
                                    {effect.name}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryClass}`}>
                                        {effect.category}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${onsetClass}`}>
                                        <Clock size={12} className="inline mr-1" />
                                        {effect.onset}
                                    </span>
                                    {effect.bodyApplicability && effect.bodyApplicability !== 'universal' && (
                                        <span className="px-3 py-1 rounded-full text-sm bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                            <Users size={12} className="inline mr-1" />
                                            {effect.bodyApplicability}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Summary */}
                        {effect.summary && (
                            <p className="text-cream/70 text-lg leading-relaxed mt-4">
                                {effect.summary}
                            </p>
                        )}

                        {/* Gold Ornate Divider */}
                        <div className="ornate-divider w-32 my-6" />

                        {/* Key Stats */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="p-4 rounded-lg bg-dark-900/50">
                                <div className="text-sm text-dark-400 mb-2">Partner Visibility</div>
                                <VisibilityBar value={effect.visibility || 5} />
                                <p className="text-xs text-dark-500 mt-2">
                                    {effect.visibility >= 7 ? 'Highly visible to partner' :
                                        effect.visibility >= 4 ? 'Somewhat noticeable' :
                                            'Mostly internal'}
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-dark-900/50">
                                <div className="flex items-center gap-2 mb-2">
                                    <Gauge size={16} className="text-amber-400" />
                                    <span className="text-sm text-dark-400">Arousal Threshold</span>
                                </div>
                                <div className="text-2xl font-bold text-cream">
                                    {effect.arousalThreshold || 50}%
                                </div>
                                <p className="text-xs text-dark-500 mt-1">
                                    Arousal level typically needed to trigger
                                </p>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Description */}
                {effect.description && (
                    <section className="mb-8">
                        <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-3 tracking-wide">Description</h2>
                        <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{effect.description}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Page Sections */}
                <PageSection
                    title="Deep Dive"
                    content={getSectionContent('deep_dive')}
                    icon={BookOpen}
                />
                <PageSection
                    title="Recognition Signs"
                    content={getSectionContent('recognition_signs')}
                    icon={Eye}
                />
                <PageSection
                    title="Partner Visibility"
                    content={getSectionContent('partner_visibility')}
                    icon={Users}
                />
                <PageSection
                    title="Individual Variations"
                    content={getSectionContent('individual_variations')}
                    icon={Sparkles}
                />
                <PageSection
                    title="Research Basis"
                    content={getSectionContent('research_basis')}
                    icon={Heart}
                />

                {/* Sub-Variants */}
                {effect.childVariants && effect.childVariants.length > 0 && (
                    <section className="mb-6 p-5 rounded-xl bg-[#0A0A0A]/60 border border-wine-800/20">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-[#C4A35A]" />
                            <h3 className="text-lg font-fantasy font-semibold text-cream/90 tracking-wide">Variants of {effect.name}</h3>
                        </div>
                        <div className="space-y-2">
                            {effect.childVariants.map(variant => (
                                <div key={variant.id} className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors">
                                    <Link to={`/effect/${variant.id}`} className="text-white hover:text-wine-300 transition-colors flex-1">
                                        {variant.name}
                                        {variant.category && <span className="ml-2 text-sm text-dark-500">{variant.category}</span>}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default ArousalEffectDetailPage;
